import React, { useEffect, useRef, useState } from "react";
import { FiSend, FiPaperclip, FiFile, FiLoader, FiArrowLeft } from "react-icons/fi";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { clearUnread } from "../../redux/chatSlice";
import useChat from "../../hooks/useChat";

const API_URL = process.env.REACT_APP_API_URL;

const getCurrentUserId = () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.id || payload._id || payload.sub || null;
  } catch {
    return null;
  }
};

const formatTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
};

const isSameDay = (a, b) => {
  if (!a || !b) return false;
  const da = new Date(a);
  const db = new Date(b);
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
};

const formatDayLabel = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const today = new Date();

  if (isSameDay(date, today)) return "Today";

  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const ChatWindow = ({ request, relatedRequestIds, otherPartyName, onBack }) => {
  const activeRequestId = request?._id || request?.id;
  const requestIds = relatedRequestIds?.length
    ? relatedRequestIds
    : activeRequestId ? [activeRequestId] : [];
  const dispatch = useDispatch();
  const { messages, connected, historyLoading, sendMessage, sendFile, markAsRead } = useChat(requestIds, activeRequestId);
  const [text, setText] = useState("");
  const [uploading, setUploading] = useState(false);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const myId = getCurrentUserId();

  useEffect(() => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = 0;
  }, [messages]);

  useEffect(() => {
    if (messages.length > 0) {
      markAsRead();
      if (activeRequestId) dispatch(clearUnread({ requestId: activeRequestId }));
    }
  }, [messages.length, markAsRead, activeRequestId, dispatch]);

  const handleSend = () => {
    if (!text.trim()) return;
    if (!connected) {
      toast.error("Not connected. Please wait and try again.");
      return;
    }
    sendMessage(text.trim());
    setText("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (!activeRequestId) {
      toast.error("Conversation not selected.");
      return;
    }

    const isImage = file.type.startsWith("image/");
    const formData = new FormData();
    formData.append("requestId", activeRequestId);
    formData.append("file", file);
    setUploading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API_URL}/chat/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.location || res.data?.imageUrl || res.data?.url;
      if (!url) throw new Error(`No URL in response: ${JSON.stringify(res.data)}`);
      sendFile({ attachmentUrl: url, fileName: file.name, mimeType: file.type, messageType: isImage ? "image" : "file" });
    } catch (err) {
      console.error("[Upload] error:", err.response?.data || err.message);
      toast.error("File upload failed.");
    } finally {
      setUploading(false);
    }
  };

  if (!request) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 text-gray-300">
        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
        </svg>
        <p className="text-sm text-gray-400">Select a conversation to start messaging</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full w-full min-w-0 bg-white">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3 shrink-0 bg-white">
        {onBack && (
          <button
            onClick={onBack}
            className="md:hidden -ml-1 p-1.5 rounded-lg text-gray-500 hover:bg-gray-100 transition-colors"
          >
            <FiArrowLeft className="w-5 h-5" />
          </button>
        )}
        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#E5D3DD] to-[#c9a8c0] flex items-center justify-center text-sm font-semibold text-[#6b3f5e] shrink-0">
          {otherPartyName?.charAt(0)?.toUpperCase()}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 truncate leading-tight">{otherPartyName}</p>
          <div className="flex items-center gap-1.5 mt-0.5">
            <div className={`w-1.5 h-1.5 rounded-full ${connected ? "bg-emerald-400" : "bg-gray-300"}`} />
            <span className={`text-[11px] ${connected ? "text-emerald-500" : "text-gray-400"}`}>
              {connected ? "Online" : "Connecting..."}
            </span>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={messagesContainerRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-2 flex flex-col-reverse" style={{ background: "#faf9fb" }}>
        {historyLoading ? (
          <div className="flex justify-center pt-10">
            <FiLoader className="w-5 h-5 text-[#7a4f6a] animate-spin" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-400">
            <p className="text-sm">No messages yet</p>
            <p className="text-xs text-gray-300">Say hello! 👋</p>
          </div>
        ) : (
          <>
            {[...messages].reverse().map((msg, i, reversed) => {
              const isMe = msg.senderId === myId || msg.senderId?._id === myId;
              const showDaySeparator = i === reversed.length - 1 || !isSameDay(msg.createdAt, reversed[i + 1]?.createdAt);
              const showTime = i === 0 ||
                new Date(msg.createdAt) - new Date(reversed[i - 1]?.createdAt) > 5 * 60 * 1000;
              return (
                <div key={msg._id || i} className={`flex flex-col ${isMe ? "items-end" : "items-start"}`}>
                  {showDaySeparator && (
                    <div className="self-center my-2 px-2 py-1 rounded-full bg-gray-100 text-[11px] text-gray-500">
                      {formatDayLabel(msg.createdAt)}
                    </div>
                  )}
                  <div
                    className={`max-w-[75%] md:max-w-[65%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
                      isMe
                        ? "bg-[#7a4f6a] text-white rounded-br-md shadow-sm"
                        : "bg-white text-gray-800 rounded-bl-md shadow-sm border border-gray-100"
                    }`}
                  >
                    {msg.messageType === "image" ? (
                      <img src={msg.attachmentUrl} alt={msg.fileName || "image"}
                        className="max-w-full rounded-xl max-h-56 object-cover" />
                    ) : msg.messageType === "file" ? (
                      <a href={msg.attachmentUrl} target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-2 underline underline-offset-2">
                        <FiFile className="w-4 h-4 shrink-0" />
                        {msg.fileName || "File"}
                      </a>
                    ) : (
                      <p className="whitespace-pre-wrap break-words">{msg.content}</p>
                    )}
                  </div>
                  {showTime && (
                    <span className={`text-[10px] mt-1 px-1 ${isMe ? "text-gray-400" : "text-gray-400"}`}>
                      {formatTime(msg.createdAt)}
                    </span>
                  )}
                </div>
              );
            })}
            <div />
          </>
        )}
      </div>

      {/* Input */}
      <div className="px-3 py-2.5 border-t border-gray-100 bg-white shrink-0">
        <div className="flex items-end gap-2 bg-gray-50 rounded-2xl px-3 py-2 border border-gray-200 focus-within:border-[#c9a8c0] focus-within:bg-white transition-colors">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
            accept="image/*,.pdf,.doc,.docx"
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="p-1 text-gray-400 hover:text-[#7a4f6a] transition-colors disabled:opacity-50 shrink-0 mb-0.5"
          >
            {uploading ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPaperclip className="w-4 h-4" />}
          </button>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message..."
            rows={1}
            className="flex-1 resize-none bg-transparent text-sm text-gray-700 placeholder-gray-400 focus:outline-none max-h-28 min-h-[22px] py-0.5"
            style={{ lineHeight: "1.5" }}
          />
          <button
            onClick={handleSend}
            disabled={!text.trim() || !connected}
            className="p-1.5 bg-[#7a4f6a] text-white rounded-xl hover:bg-[#6a3f5a] transition-all disabled:opacity-35 disabled:cursor-not-allowed shrink-0 mb-0.5"
          >
            <FiSend className="w-3.5 h-3.5" />
          </button>
        </div>
        <p className="text-[10px] text-gray-300 text-center mt-1">Enter to send · Shift+Enter for new line</p>
      </div>
    </div>
  );
};

export default ChatWindow;
